import mitt, {Emitter} from 'mitt';
import {
  CheckboxForOptionMissingError,
  ConfigurationMissingError,
  OptionForIdentifierMissingError,
  UnknownViewError,
  ViewContainerMissingError
} from './errors';
import CookieUtility from './utilities/cookie-utility';
import GeneralUtility from './utilities/general-utility';
import Group from './group';
import Option from './option';
import EssentialGroup from './essential-group';
import Logger from './utilities/logger';
import ModalView from './views/modal-view';
import TopView from './views/top-view';
import AbstractView from './views/abstract-view';
import Edit from './components/edit';
import OptionControl from './components/option-control';

/**
 * API providing access to the consent management methods. The suffix `Api` is used, as otherwise webpack will not
 * create a proper usable global `A11yConsent` object for external access. Has something to do with the `index.ts` and
 * the main`A11yConsent` export.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export default class A11yConsentApi {
  // Name of the cookie marking interaction with the consent view
  private readonly interactionCookieName = 'a11y-consent-interaction';

  // Emits consent related events
  public readonly emitter: Emitter<any> = mitt<any>();

  // Current page UID
  public readonly pageUid: number;

  // Consent groups
  public readonly groups: Group[] = new Array<Group>();

  // Essential consent groups
  public readonly essentialGroups: EssentialGroup[] = new Array<EssentialGroup>();

  // Main plugin container
  private readonly mainContainer: HTMLElement | null = null;

  // List plugin container
  private readonly listContainer: HTMLElement | null = null;

  // Consent view
  public readonly view: AbstractView | null = null;

  // Edit component
  public readonly edit: Edit | null = null;

  // Option checkboxes
  public readonly optionCheckboxes: NodeListOf<HTMLInputElement>;

  // Option controls
  public readonly optionControls: Map<string, OptionControl> = new Map<string, OptionControl>();

  // Hint container
  public readonly hintContainer: HTMLElement | null = null;

  constructor() {
    // Make sure, that the global settings are present, as they might have been removed by an AdBlocker
    if (typeof A11yConsentConfiguration === 'undefined' || !A11yConsentConfiguration) {
      throw new ConfigurationMissingError();
    }
    Logger.debug('api', 'Starting to construct.');

    // Build the consent groups and options
    this.pageUid = A11yConsentConfiguration.pageUid;
    A11yConsentConfiguration.groups.forEach(data => {
      this.groups.push(new Group(this, data));
    });
    A11yConsentConfiguration.essentialGroups.forEach(data => {
      this.essentialGroups.push(new EssentialGroup(this, data));
    });

    // Set up some basic components
    // Neither the main plugin, list plugin nor the edit component are required to be present. Users may provide some
    // very customized implementation of the A11y Consent "frontend", but still want to rely on the general API methods
    // for manipulating what consent options are enabled etc. The only thing required for the A11y Consent library to
    // work is the global A11yConsentConfiguration object.

    // Set up the main plugin
    this.mainContainer = document.querySelector<HTMLElement>('[data-ctype="a11yconsent_main"]');
    if (this.mainContainer === null) {
      Logger.warning('a11y-consent-api', 'Main plugin container not found.');
    } else {
      // Set up the consent view
      const viewContainer = this.mainContainer
        .querySelector<HTMLElement>(`[data-a11y-consent-view="${A11yConsentConfiguration.settings.view}"]`);
      if (viewContainer === null) {
        Logger.error('a11y-consent-api', 'Missing view container.');
        throw new ViewContainerMissingError();
      }
      switch (A11yConsentConfiguration.settings.view) {
        case 'Modal':
          this.view = new ModalView(this, viewContainer);
          break;
        case 'Top':
          this.view = new TopView(this, viewContainer);
          break;
        default:
          Logger.error('a11y-consent-api', 'Unknown view.');
          throw new UnknownViewError();
      }
    }

    // Set up the list plugin
    this.listContainer = document.querySelector<HTMLElement>('[data-ctype="a11yconsent_list"]');
    if (this.listContainer === null) {
      Logger.warning('a11y-consent-api', 'List plugin container not found.');
    }

    // Set up the edit component
    const editContainer = document.querySelector<HTMLElement>('#a11y-consent-edit');
    if (editContainer === null) {
      Logger.warning('a11y-consent-api', 'Edit container not found.');
    } else {
      this.edit = new Edit(this, editContainer);
    }

    // Set up option controls
    this.optionCheckboxes = document.querySelectorAll<HTMLInputElement>('[data-a11y-consent-option]');
    if (this.optionCheckboxes.length === 0) {
      Logger.warning('a11y-consent-api', 'No consent option checkboxes found.');
    } else {
      for (const checkbox of this.optionCheckboxes) {
        if (!checkbox.dataset.a11yConsentOption) {
          throw new CheckboxForOptionMissingError();
        }
        const identifier = checkbox.dataset.a11yConsentOption;
        const option = this.getOption(identifier);
        if (option === null) {
          throw new OptionForIdentifierMissingError();
        }
        this.optionControls.set(identifier, new OptionControl(this, option, checkbox));
      }
    }

    // Set up the hint container
    this.hintContainer = document.querySelector('[data-a11y-consent-hint]');

    Logger.debug('api', 'Finished constructing.');
  }

  /**
   * Initializes all necessary components. After this method is done, we can start to ask the user for consent.
   */
  public initialize(): void {
    Logger.debug('api', 'Starting to initialize components.');
    // If A11y Consent has never been interacted with or any option has not been interacted with, show the view
    const showView = !this.isInteracted() || !this.allOptionsInteracted();

    // Decode all content related to currently enabled consent options
    this.decodeAll();

    // Show consent view, if it was never interacted with before. Do not show, if the current page is explicitly
    // excluded from requiring consent.
    if (!this.isCurrentPageExcluded()) {
      if (showView) {
        Logger.info('api', 'Show consent view.');
        this.view?.show();
      } else {
        Logger.info('api', 'No need to show the consent view.');
      }
    } else {
      Logger.info('api', 'Current page is excluded from requiring consent.');
    }

    this.initializeGlobalEmitters();
    this.initializeGlobalActions();
    this.initializeEnableActions();

    Logger.debug('api', 'Finished initializing components.');
    this.emitter.emit('ready', this);
  }

  /**
   * Initializes emitters for some global elements. This includes e.g. A11y Consent "accept all" or "reject all"
   * buttons. These buttons may be used multiple times in different views, but each of those buttons should always lead
   * to the same actions being triggered.
   */
  private initializeGlobalEmitters(): void {
    const acceptAllActions = document.querySelectorAll<HTMLButtonElement>('[data-a11y-consent-accept-all]');
    for (const action of acceptAllActions) {
      action.addEventListener('click', () => {
        Logger.info('api', 'Accept all clicked.');
        this.emitter.emit('acceptAllClicked', action);
      });
    }

    const rejectAllActions = document.querySelectorAll<HTMLButtonElement>('[data-a11y-consent-reject-all]');
    for (const action of rejectAllActions) {
      action.addEventListener('click', () => {
        Logger.info('api', 'Reject all clicked.');
        this.emitter.emit('rejectAllClicked', action);
      });
    }

    const saveActions = document.querySelectorAll<HTMLButtonElement>('[data-a11y-consent-save]');
    for (const action of saveActions) {
      action.addEventListener('click', () => {
        Logger.info('api', 'Save clicked.');
        this.emitter.emit('saveClicked', action);
      });
    }

    const resetActions = document.querySelectorAll<HTMLButtonElement>('[data-a11y-consent-reset]');
    for (const action of resetActions) {
      action.addEventListener('click', () => {
        Logger.info('api', 'Reset clicked.');
        this.emitter.emit('resetClicked', action);
      });
    }

    const showActions = document.querySelectorAll<HTMLButtonElement>('[data-a11y-consent-show]');
    for (const action of showActions) {
      action.addEventListener('click', () => {
        Logger.info('api', 'Show clicked.');
        this.emitter.emit('showClicked', action);
      });
    }

    const hideActions = document.querySelectorAll<HTMLButtonElement>('[data-a11y-consent-hide]');
    for (const action of hideActions) {
      action.addEventListener('click', () => {
        Logger.info('api', 'Hide clicked.');
        this.emitter.emit('hideClicked', action);
      });
    }
  }

  /**
   * Initializes actions for some global events. This includes e.g. the "accept all" or "reject all" buttons. These
   * buttons may be used multiple times in different views, but each of those buttons should always lead to the same
   * actions being triggered.
   */
  private initializeGlobalActions(): void {
    this.emitter.on('acceptAllClicked', () => {
      this.acceptAll();
    });

    this.emitter.on('rejectAllClicked', () => {
      this.rejectAll();
    });

    this.emitter.on('saveClicked', () => {
      this.save();
    });

    this.emitter.on('resetClicked', () => {
      this.reset();
    });

    this.emitter.on('showClicked', () => {
      this.view?.show();
    });

    this.emitter.on('hideClicked', () => {
      this.view?.hide();
    });
  }

  /**
   * Initializes actions for enabling a specific consent option, e.g. via a button or link.
   */
  private initializeEnableActions(): void {
    const actions = document.querySelectorAll<HTMLElement>('[data-a11y-consent-enable]');
    for (const action of actions) {
      const identifier = action.dataset.a11yConsentEnable;
      if (!identifier) {
        Logger.error('a11y-consent-api', 'Enable action is missing an identifier.', action);
        return;
      }
      const option = this.getOption(identifier);
      if (!option) {
        Logger.warning('a11y-consent-api', `No consent option exists for identifier "${identifier}".`, action);
        return;
      }

      action.addEventListener('click', () => {
        this.emitter.emit('enableClicked', {api: this, action: action, option: option});

        // If there is a placeholder, keep a reference to it, to later set the focus to its parent element
        const placeholder = action.closest<HTMLElement>('[data-a11y-consent-placeholder]');
        const parentContainer = placeholder?.parentElement;
        const enabledText = placeholder?.dataset.a11yConsentPlaceholderEnabledText ?? null;

        option.setEnabled(true);
        option.emitter.on('decoded', () => {
          // Insert the enabled text in the hint container for a short amount of time for screen-readers
          if (enabledText !== null && this.hintContainer) {
            this.hintContainer.innerText = enabledText;
            setTimeout(() => {
              if (this.hintContainer) {
                this.hintContainer.innerText = '';
              }
            }, 20000);
          }

          if (parentContainer) {
            parentContainer.focus();
          }
        });

        this.emitter.emit('enable', {api: this, action: action, option: option});
      });
    }
  }

  /**
   * Accepts all consent options and hides the consent view, if available. After this is executed, the user will not see
   * the consent view again.
   */
  public acceptAll(): void {
    Logger.debug('api', 'Accept all.');

    this.enableOrDisableAllOptions(true);
    this.setOrDeleteInteractionCookie();
    this.executeScript('acceptAll');
    this.emitter.emit('acceptAll', this);

    if (A11yConsentConfiguration.settings.reloadAfterSaving) {
      window.location.reload();
    }
  }

  /**
   * Rejects all consent options and hides the consent view, if available. After this is executed, the user will not see
   * the consent view again.
   */
  public rejectAll(): void {
    Logger.debug('api', 'Reject all.');

    this.enableOrDisableAllOptions(false);
    this.setOrDeleteInteractionCookie();
    this.executeScript('rejectAll');
    this.emitter.emit('rejectAll', this);

    if (A11yConsentConfiguration.settings.reloadAfterSaving) {
      window.location.reload();
    }
  }

  /**
   * Saves the currently chosen consent options. If a consent view is available and some option checkboxes have been
   * interacted with, those checkboxes will define the newly chosen consent settings.
   */
  public save(): void {
    Logger.debug('api', 'Save.');

    this.enableCheckedAndDisableUncheckedOptions();
    this.setOrDeleteInteractionCookie();
    this.executeScript('save');
    this.emitter.emit('save', this);

    if (A11yConsentConfiguration.settings.reloadAfterSaving) {
      window.location.reload();
    }
  }

  /**
   * Completely resets the user consent settings. After this function was executed, the consent process will start from
   * the beginning for a user.
   */
  public reset(): void {
    Logger.debug('api', 'Reset.');

    this.deleteAllCookies();
    this.emitter.emit('reset', this);
  }

  /**
   * Enables or disables all consent options. This will also automatically trigger the decoding of corresponding
   * content.
   *
   * @param {boolean} enabled True to enable all options, otherwise false
   */
  public enableOrDisableAllOptions(enabled: boolean): void {
    Logger.debug('api', 'Set all options to ' + (enabled ? 'enabled' : 'disabled') + '.');

    for (const group of this.groups) {
      group.setOptionsEnabled(enabled);
    }
  }

  /**
   * Enables all consent options, where their related option control is currently checked. All options, where the
   * checkbox is unchecked, will be disabled.
   */
  public enableCheckedAndDisableUncheckedOptions(): void {
    Logger.debug('api', 'Enable checked and disable unchecked option controls.');

    for (const control of this.optionControls.values()) {
      control.option.setEnabled(control.isChecked());
    }
  }

  /**
   * Gets a consent option by its identifier.
   *
   * @param {string} identifier Identifier of the consent option to get
   * @returns {Option|null} The option for the given identifier or null, if no option was found
   */
  public getOption(identifier: string): Option | null {
    for (const group of this.groups) {
      for (const key in group.options) {
        const option = group.options[key];
        if (identifier === key) {
          return option;
        }
      }
    }
    return null;
  }

  /**
   * Updates the checkbox state of all option controls, meaning their checked state will fit if they are currently
   * enabled or disabled by the user.
   */
  public updateOptionControls(): void {
    Logger.debug('api', 'Update option controls.');

    for (const control of this.optionControls.values()) {
      control.update();
    }
  }

  /**
   * Checks if the interaction cookie is present. If this cookie is not found, the consent view needs to be shown.
   *
   * @returns {boolean} True when the cookie is present, otherwise false
   */
  public isInteracted(): boolean {
    if (CookieUtility.get(this.interactionCookieName) !== null) {
      Logger.debug('api', 'Found interaction cookie.');
      return true;
    } else {
      Logger.debug('api', 'Interaction cookie not found.');
      return false;
    }
  }

  /**
   * Checks if all options have been interacted with, meaning they have either been enabled or disabled by a user. If
   * an option has not been interacted with, the consent view needs to be shown.
   *
   * @returns {boolean} True when at least one option has not been interacted with, otherwise false
   */
  public allOptionsInteracted(): boolean {
    for (const group of this.groups) {
      for (const identifier in group.options) {
        const option = group.options[identifier];
        if (!option.isInteracted()) {
          Logger.debug('api', `Option "${identifier}" has not been interacted with.`);
          return false;
        }
      }
    }
    Logger.debug('api', 'All options have been interacted with.');
    return true;
  }

  /**
   * Sets or deletes a cookie marking the consent process as "interacted". This means, that the user has enabled or
   * disabled any amount of cookies, or the user did not allow any cookies at all (besides the essential cookies). If
   * this interaction-cookie is present, the consent view does not need to be shown again.
   *
   * @param {boolean} [set=true] True to set the cookie, false to delete it
   */
  public setOrDeleteInteractionCookie(set: boolean = true): void {
    if (set) {
      Logger.debug('api', 'Setting interaction cookie.');
      CookieUtility.set(
        this.interactionCookieName,
        '1',
        A11yConsentConfiguration.settings.cookieDomain
      );
    } else {
      Logger.debug('api', 'Deleting interaction cookie.');
      CookieUtility.delete(this.interactionCookieName, A11yConsentConfiguration.settings.cookieDomain);
    }
  }

  /**
   * Executes one of the global scripts, like the script when an "accept all" button was clicked.
   *
   * @param {string} name Name of the script to execute
   */
  public executeScript(name: string): void {
    Logger.debug('api', `Execute script "${name}".`);
    if (!Object.keys(A11yConsentConfiguration.scripts).includes(name)) {
      return;
    }
    const script = A11yConsentConfiguration.scripts[name];
    if (typeof script !== 'function') {
      return;
    }
    script();
    Logger.debug('api', `Executed script "${name}".`);
  }

  /**
   * Tries to delete cookies, that were created by consent options. This only includes cookies detectable by the consent
   * option identifier. Cookies loaded by third-party libraries will not be deleted.
   */
  public deleteAllCookies(): void {
    Logger.debug('api', 'Delete all cookies.');

    for (const group of this.groups) {
      for (const identifier in group.options) {
        CookieUtility.delete(identifier, A11yConsentConfiguration.settings.cookieDomain);
      }
    }

    this.setOrDeleteInteractionCookie(false);
  }

  /**
   * Decodes all encoded content.
   */
  public decodeAll(): void {
    Logger.debug('api', 'Decode all.');

    this.emitter.emit('decodeAll', this);
    this.groups.forEach(group => {
      group.decodeOptions();
    });
    this.emitter.emit('decodedAll', this);
  }

  /**
   * Checks if the current page is excluded from requiring consent.
   */
  public isCurrentPageExcluded(): boolean {
    return A11yConsentConfiguration.settings.excludedPages.indexOf(this.pageUid) !== -1;
  }

  /**
   * Encodes a string via the A11y Consent API.
   *
   * @param {string} string String to encode
   * @param {string} baseUrl Base URL for XHR requests
   * @returns {Promise} Response from the API
   */
  public encode(string: string, baseUrl: string): Promise<Response> {
    Logger.debug('api', `Encode "${string}".`);

    this.emitter.emit('encode', string);
    if (baseUrl === '') {
      baseUrl = GeneralUtility.getUrl();
    }
    return fetch(`${baseUrl}/?a11yConsentApi%5Bendpoint%5D%5B%5D=encode`, {
      method: 'POST',
      body: JSON.stringify({string: string})
    });
  }

  /**
   * Decodes a previously encoded string via the A11y Consent API.
   *
   * @param {string} string String to decode
   * @param {string} baseUrl Base URL for XHR requests
   * @returns {Promise} Response from the API
   */
  public decode(string: string, baseUrl: string): Promise<Response> {
    Logger.debug('api', `Decode "${string}".`);

    this.emitter.emit('decode', string);
    if (baseUrl === '') {
      baseUrl = GeneralUtility.getUrl();
    }
    return fetch(`${baseUrl}/?a11yConsentApi%5Bendpoint%5D%5B%5D=decode`, {
      method: 'POST',
      body: JSON.stringify({string: string})
    });
  }
}
